import {
    animate,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    state,
    style,
    transition,
    trigger
} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Response} from '@angular/http';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable, ReplaySubject, Subscription} from 'rxjs';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {Subject} from 'rxjs/Subject';
import * as _ from 'lodash';
import {StepScreen} from './step-screen.class';
import {FormErrors, SpecificValidationMessages} from '../../../components/forms/validation/form-validator.class';
import {
    FormManager,
    FormSubmissionStatusCode,
    FormSubmissionStatusReason
} from '../../../components/forms/form-manager.class';
import {coerceBooleanProperty} from '../../../common/value/coercion/boolean-coercion.util';
import {SectionsService} from '../../../components/sections/service/sections.service';
import {ScreensManager} from '../../../components/sections/service/screen-manager.class';
import {NavigationService} from '../../../navigation/service/navigation.service';
import {PageStateService} from '../services/page-state.service';
import {BackendError} from '../../../common/error/backend-error.class';
import assign = require('core-js/fn/object/assign');

enum NextScreenTransitioningStatus {
    NOT_TRANSITIONING = 1,
    TRANSITIONING,
    TRANSITIONING_WITHOUT_VALIDATION
}

/**
 * Created by Rodrigo Quesada on 04/12/16.
 */
@Component({
    selector: 'my-step-screen',
    template: require('./step-screen.component.html'),
    styles: [require('./step-screen.component.scss')],
    animations: [
        trigger('routeAnimation', [
            state('*',
                style({
                    opacity: 1,
                    transform: 'translateX(0)'
                })
            ),
            transition('void => fromLeft', [
                style({
                    opacity: 0,
                    transform: 'translateX(-100%)'
                }),
                animate('0.5s ease-in')
            ]),
            transition('void => fromRight', [
                style({
                    opacity: 0,
                    transform: 'translateX(100%)'
                }),
                animate('0.5s ease-in')
            ]),
        ]),
        trigger('formAnimation', [
            state('hidden',
                style({
                    height: 0,
                    opacity: 0,
                    overflow: 'hidden'
                })
            ),
            state('visible',
                style({
                    height: '*',
                    opacity: 1,
                    overflow: 'visible'
                })
            ),
            transition('hidden => visible', [
                animate('1s ease-in')
            ]),
        ])
    ]
})
export class StepScreenComponent extends StepScreen implements OnDestroy {

    // region Host Binding/listening
    @HostListener('document:keyup.arrowleft', ['$event'])
    onArrowLeft(event: KeyboardEvent) {
        if (this.navigationService.keyboardNavigationIsEnabled) this.filterOutInputFields(event, () => this.goToPreviousScreen());
    }

    @HostListener('document:keyup.arrowright', ['$event'])
    onArrowRight(event: KeyboardEvent) {
        if (this.navigationService.keyboardNavigationIsEnabled) this.filterOutInputFields(event, () => this.goToNextScreen());
    }

    // endregion

    // region Input/Output
    private readonly stepScreenModel$ = new ReplaySubject<StepScreenModel<any>>(1);

    @Input('model')
    set stepScreenModel(value: StepScreenModel<any>) {
        if (value) this.stepScreenModel$.next(value);
    }

    // endregion

    // region Form
    readonly form$ = new ReplaySubject<NgForm>(1);
    readonly formManager: FormManager<any>;

    // TODO change after migrating to reactive forms...
    @Input()
    set form(value: NgForm) {
        if (value) this.form$.next(value);
    }

    // endregion

    // region YesNoQuestion
    yesNoAnswerChange$ = new ReplaySubject<boolean | undefined>(1);
    yesNoQuestionAnswer$: Observable<boolean | undefined>;

    changeYesNoQuestionAnswer(value: boolean, event) {
        event.preventDefault();
        setTimeout(() => this.yesNoAnswerChange$.next(coerceBooleanProperty(value)));
    }

    hasYesNoQuestion$: Observable<boolean>;

    askYesNoQuestion$: Observable<boolean>;
    private _askYesNoQuestion: boolean = false;

    private _refreshAskYesNoQuestion() {
        this.askYesNoQuestion$.subscribe(value => this._askYesNoQuestion = value);
    }

    formState: string = 'none';

    private _refreshFormState() {
        this.askYesNoQuestion$.subscribe(value => this.formState = value ? 'hidden' : 'visible');
    }

    // endregion

    screenChange$: Observable<boolean>;
    goToPreviousScreen$: Observable<boolean>;
    goToNextScreenAction$: Observable<boolean>;
    goToNextScreenWithoutSaving$: Observable<boolean>;

    nextScreenTransitioningStatus: NextScreenTransitioningStatus = NextScreenTransitioningStatus.NOT_TRANSITIONING;

    // region Subscriptions
    readonly subscriptions: Subscription[] = [];
    // endregion

    nextScreenTransitioningStatuses = NextScreenTransitioningStatus;

    verifiable: boolean = false;

    constructor(private readonly _el: ElementRef,
                readonly router: Router,
                readonly sectionsService: SectionsService,
                readonly pageStateService: PageStateService,
                readonly navigationService: NavigationService,
                fmgm: FormManager.InjectedMembers) {
        super();

        this.formManager = new FormManager(
            fmgm,
            this.form$,
            this.stepScreenModel$.map(m => () => m.submitData()),
            this.stepScreenModel$.map(m => m.specificValidationMessages)
        );

        this.screenChange$ = this.stepScreenModel$.switchMap(m => m.screensManager ? m.screensManager.screenChange$ : Observable.of(true))
            .publishReplay(1).refCount();

        this.screenChange$.subscribe(() => {
            this.formState = 'none';
            this.yesNoAnswerChange$.next(undefined);
            this.formManager.reset();
        });

        this.subscriptions.push(combineLatest(this.stepScreenModel$, this.screenChange$).subscribe(([m]) => {
            this.subscriptions.push(
                m.dataModel$.subscribe(stepData => {
                    if (stepData && stepData.exists && stepData.malpractice) {
                        this.verifiable = stepData.malpractice.verifiable;
                    } else {
                        this.verifiable = stepData && stepData.exists && stepData.verifiable;
                    }
                })
            );
            m.loadData();
        }));

        this.hasYesNoQuestion$ = combineLatest(this.stepScreenModel$, this.screenChange$)
            .switchMap(([model]) => model.hasYesNoQuestion())
            .publishReplay(1).refCount();

        this.yesNoQuestionAnswer$ = this.yesNoAnswerChange$.withLatestFrom(this.form$.startWith(undefined))
            .filter(([value, form]) => value !== false || !!form && !form.dirty || confirm('The form has not been saved, are you sure you want to continue?'))
            .map(([value]) => value)
            .publishReplay(1).refCount();

        this.askYesNoQuestion$ = combineLatest(this.hasYesNoQuestion$, this.yesNoQuestionAnswer$.startWith(undefined))
            .map(([hasYesNoQuestion, yesNoAnswer]) => hasYesNoQuestion && !yesNoAnswer)
            .publishReplay(1).refCount();

        this.yesNoQuestionAnswer$.subscribe(value => { if (value === false) this.goToNextScreenWithoutSaving(); });

        this.formManager.displayedErrors$.combineLatest(this.stepScreenModel$)
            .subscribe(([formErrors, m]) => m.formErrors = formErrors);
        this.stepScreenModel$.subscribe(m => m.setFormErrorsObservable(this.formManager.displayedErrors$));
        this.form$.combineLatest(this.stepScreenModel$)
            .subscribe(([form, m]) => m.form = form);

        this._refreshFormState();
        this._refreshAskYesNoQuestion();

        this.goToPreviousScreen$ = this.stepScreenModel$.switchMap(m => m.goToPreviousScreen$)
            .publishReplay(1).refCount();
        this.goToPreviousScreen$.subscribe(() => this.goToPreviousScreen());

        this.goToNextScreenAction$ = this.stepScreenModel$.switchMap(m => m.goToNextScreen$)
            .publishReplay(1).refCount();
        this.goToNextScreenAction$.subscribe(() => this.goToNextScreen());

        this.goToNextScreenWithoutSaving$ = this.stepScreenModel$.switchMap(m => m.goToNextScreenWithoutSaving$)
            .publishReplay(1).refCount();
        this.goToNextScreenWithoutSaving$.subscribe(() => this.goToNextScreenWithoutSaving());
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private filterOutInputFields(event: KeyboardEvent, callback: (event: KeyboardEvent) => void) {
        if (!event.srcElement || event.srcElement.tagName.toLowerCase() !== 'input') callback(event);
    }

    showGoToNextScreenButton(): boolean {
        return super.hasNextScreen();
    }

    showSaveAndSkipButton(): boolean {
        return !this._askYesNoQuestion && super.hasNextScreen();
    }

    goToNextScreen() {
        if (this.hasNextScreen() && this.nextScreenTransitioningStatus === NextScreenTransitioningStatus.NOT_TRANSITIONING) {
            this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.TRANSITIONING;
            if (!this._askYesNoQuestion) {
                this.formManager.save().subscribe(result => {
                    const resultStatus = result.status;
                    if (resultStatus.code === FormSubmissionStatusCode.SUBMITTED) {
                        this.goToNextScreenWithoutSaving(true);
                    }
                    else if (resultStatus.code === FormSubmissionStatusCode.NOTSUBMITTED) {
                        if (resultStatus.reason === FormSubmissionStatusReason.NOT_DIRTY) {
                            this.formManager.hasErrors$.first().subscribe(hasErrors => {
                                if (!hasErrors) {
                                    this.goToNextScreenWithoutSaving(true);
                                } else {
                                    this.formManager.displayedErrors$.first().subscribe(it => {
                                        this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.NOT_TRANSITIONING;
                                        this._focusFirstControl(it);
                                    });
                                }
                            });
                        } else if (resultStatus.reason === FormSubmissionStatusReason.HAS_ERRORS) {
                            this.formManager.hasErrors$.first().subscribe(hasErrors => {
                                this.formManager.displayedErrors$.first().subscribe(it => {
                                    this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.NOT_TRANSITIONING;
                                    this._focusFirstControl(it);
                                });
                            });
                        }
                    } else this._handleBackendError(result);
                });
            }
            else this.goToNextScreenWithoutSaving(true);
        }
    }

    saveAndSkip() {
        if (this.nextScreenTransitioningStatus === NextScreenTransitioningStatus.NOT_TRANSITIONING) {
            this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.TRANSITIONING_WITHOUT_VALIDATION;
            this.formManager.save(true).subscribe(result => {

                if (result.status.code === FormSubmissionStatusCode.ERROR) this._handleBackendError(result);
                else this.goToNextScreenWithoutSaving(true);
            });
        }
    }

    private _handleBackendError(result) {
        this.stepScreenModel$.first().subscribe(m => m.error(result.error));
        this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.NOT_TRANSITIONING;
    }

    private goToNextScreenWithoutSaving(continueTransitioning: boolean = false) {
        if (super.hasNextScreen() && (continueTransitioning || this.nextScreenTransitioningStatus === NextScreenTransitioningStatus.NOT_TRANSITIONING)) {
            if (!continueTransitioning) this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.TRANSITIONING_WITHOUT_VALIDATION;
            super.goToNextScreen$().subscribe(() => this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.NOT_TRANSITIONING);
        }
        else this.nextScreenTransitioningStatus = NextScreenTransitioningStatus.NOT_TRANSITIONING;
    }

    private _focusFirstControl(formErrors: FormErrors) {
        setTimeout(() => $(this._el.nativeElement)
            .find('input, textarea, select')
            .filter((i, input) => _.includes(Object.keys(formErrors), (input as HTMLInputElement).name))
            .first()
            .focus());
    }
}

// region Other Classes
export interface YesNoQuestionModelData {
    answer$: Observable<boolean>;
}

export interface StepScreenModelData<M> {
    readonly initialDataModel: M;
    readonly screensManager?: ScreensManager;
    _loadDataFn: () => Observable<M | undefined>;
    _submitDataFn: (M) => Observable<Response>;
    _error?: (BackendError?) => void;
    _hasYesNoQuestion?: () => Observable<boolean>;
    readonly specificValidationMessages: SpecificValidationMessages;
}

export class StepScreenModel<M> implements StepScreenModelData<M> {

    readonly _loadDataFn: () => Observable<M | undefined>;
    readonly _submitDataFn: (M) => Observable<Response>;
    readonly _error?: (BackendError?) => void;
    readonly _hasYesNoQuestion?: () => Observable<boolean>;

    readonly dataModel$: BehaviorSubject<M>;
    readonly dataModelExists$: Subject<boolean>;

    get dataModel(): M {
        return this.dataModel$.getValue();
    }

    set dataModel(value: M) {
        this.dataModel$.next(value);
        this.dataModelExists$.next(true);
    }

    readonly initialDataModel: M;
    readonly screensManager?: ScreensManager;

    goToPreviousScreen$ = new ReplaySubject<boolean>(1);
    goToNextScreen$ = new ReplaySubject<boolean>(1);
    goToNextScreenWithoutSaving$ = new ReplaySubject<boolean>(1);

    // region Form
    readonly specificValidationMessages: SpecificValidationMessages;
    form: NgForm;
    private readonly _formErrorsObservables = new BehaviorSubject<Observable<FormErrors>>(Observable.of({}));
    readonly formErrors$: Observable<FormErrors> = this._formErrorsObservables.flatMap(it => it);
    formErrors: FormErrors = {};

    // endregion

    constructor(data: StepScreenModelData<M>) {
        assign(this, data);
        this.dataModel$ = new BehaviorSubject(this.initialDataModel);
        this.dataModelExists$ = new ReplaySubject<boolean>(1);
    }

    loadData() {
        this._loadDataFn().subscribe(data => {
            if (data !== undefined) this.dataModel = data;
        });
    }

    submitData(): Observable<Response> {
        return this._submitDataFn(this.dataModel);
    }

    error(error?: BackendError) {
        if (this._error) this._error(error);
    }

    hasYesNoQuestion(): Observable<boolean> {
        return this._hasYesNoQuestion ? this._hasYesNoQuestion() : Observable.of(false);
    }

    goToPreviousScreen() {
        this.goToPreviousScreen$.next(true);
    }

    goToNextScreen() {
        this.goToNextScreen$.next(true);
    }

    goToNextScreenWithoutSaving() {
        this.goToNextScreenWithoutSaving$.next(true);
    }

    setFormErrorsObservable(observable: Observable<FormErrors>) {
        this._formErrorsObservables.next(observable);
    }
}

// endregion



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/shared/step/step-screen.component.ts